<template>
  <app-modal container-class="modal-sml confirmation-modal">
    <div slot="header">
      <h3>{{ headerText }}</h3>
    </div>
    <div slot="body">
      <p>{{ message }}</p>
      <div v-if="!showDeleteConfirmation.visible">
        <div v-if="cloningError">
          <p class="error-msgs">{{ cloningError }}</p>
        </div>
        <form-renderer
          id="clone-form"
          :model.sync="model"
          :fields.sync="formFields"
          :fieldsAdditions="formFieldsAdditions"
          :showMessageNotifications="false"
          :show-submit="false"
          formElementClass="flex items-end justify-start"
          formWrapperClass="flex-grow mr10"
          @updateModel="(value, field) => updateModel(value, field)"
        />
      </div>
    </div>
    <div slot="footer">
      <div class="btn-container">
        <button v-if="showDeleteConfirmation.visible" class="btn btn-danger btn-sm mr10" tabindex="0" v-on:click="deleteTags" :disabled="buttonsDisabled">
          {{ $t('messages.confirm_delete.delete') }}
        </button>
        <button v-else class="btn button-save btn-sm mr10" v-on:click="submit" :disabled="buttonsDisabled">
          {{ $t("confirmation.confirm") }}
        </button>
        <button class="btn btn-default btn-sm" tabindex="1" v-on:click="$emit('close')" :disabled="buttonsDisabled">
          {{ $t("confirmation.cancel") }}
        </button>
      </div>
    </div>
  </app-modal>
</template>

<script>

// Plugins
import { mapState ,mapActions } from 'vuex'

// Mixins
import storeHelper from '../../../mixins/boolean-search/storeHelper'
import notifyMessages from '../../../mixins/boolean-search/notifyMessages'

// Components
import FormRenderer from '../../form-renderer.vue';
import i18n from '../../../i18n';
import Modal from '../../modal.vue';

export default {
  name: "BatchCloneDeleteModal",

  props: {
    data: {
      type: Object,
      required: true
    },
    showDeleteConfirmation: {
      type: Object,
    },
    cancelSelection: {
      type: Function,
    },
  },

  mixins: [ storeHelper, notifyMessages ],

  data() {
    return {
      buttonsDisabled: false,
      model: { name: '', projectId: null, client_id: null },
    }
  },

  computed: {

    ...mapState({
      project_id: state => state.booleanSearch.project_id,
      client_id: state => state.booleanSearch.client_id,
      projects: state => state.booleanSearch.projects,
      cloningError: state => state.booleanSearch.clonedError,
      tag_library: state => state.booleanSearch.tag_library,
    }),

    projectsData() { return this.projects.data },

    tagsSelected(){
      return Object.values(this.data).reduce((acc, data) => {
        const tags = Array.from(data.tags.values());
        return acc.concat(tags);
      }, []);
    },

    message() {
      if (this.showDeleteConfirmation.visible){
        return i18n.t('booleanSearch.select.confirm_delete', [this.showDeleteConfirmation.count, this.showDeleteConfirmation.count > 1 ? 'tags' : 'tag']);
      } else {
        return i18n.t('booleanSearch.select.confirm_clone', [this.tagsSelected.length, this.tagsSelected.length == 1 ? 'tag' : 'tags']);
      }
    },

    headerText() {
      if (this.showDeleteConfirmation.visible){
        return i18n.t('messages.confirm_delete.title_selected');
      } else {
        return i18n.t('booleanSearch.select.clone_selected');
      }
    },

    formFields() {
        return ["boolProjectId"];
    },
    formFieldsAdditions() {
      return {
        boolProjectId: {
          options: this.projectsData,
          onSearchChange: (name) => {
            this.searchProjects(name);
          }
        }
      };
    }
  },

  methods: {
    ...mapActions({
      getProjects : "booleanSearch/getPaginatedResource",
      batchCloneTags: "booleanSearch/batchCloneTags",
      batchDeleteTags: "booleanSearch/batchDeleteTags",
    }),
    searchProjects(value) {
      if(!value && this.model.projectId) return
      this.setFilterParam('projects', 'query', value)
      this.resetPagination('projects')
      this.getProjects({key: 'projects', url: `projects/${this.project_id}/search`})
    },
    submit() {
      this.buttonsDisabled = true;
      const newProjectId = this.model.projectId;
      if(!newProjectId) {
        this.$store.commit('booleanSearch/SET_CLONING_ERROR', this.$t('booleanSearch.select.no_project_selected'));
        this.buttonsDisabled = false;
        return;
      }
      this.batchCloneTags({
        tags: this.tagsSelected, 
        projectId: this.project_id, 
        newProjectId, 
        clientId: this.client_id,
      }).then((data) => {
        if(!data.valid){
            this.notifyStoreError(this.$t('booleanSearch.select.cloned_failed'));
            return;
          }
        this.notifyStoreSuccess(this.$t('booleanSearch.select.cloned_successfully'));
        this.$emit('close');
      }).finally(() => {
        this.buttonsDisabled = false;
      });
    },
    deleteTags(){
      this.buttonsDisabled = true;
      const removedFromEachCategory = {};
      const selectedTags = Object.entries(this.data).reduce((acc, [category, data]) => {
        const tags = Array.from(data.tags.keys());
        if(tags.length) removedFromEachCategory[category] = tags.length;
        return acc.concat(tags);
      }, []);
      this.batchDeleteTags({
        tagsIds: selectedTags,
        projectId: this.project_id,
        removedFromEachCategory
      }).then(() => {
        this.notifyStoreSuccess(this.$t('booleanSearch.select.deleted_successfully'));
        this.cancelSelection();
        this.showDeleteConfirmation.visible = false;
        this.$emit('close');
      }).catch(() => {
        this.notifyStoreError(this.$t('booleanSearch.select.delete_failed'));
      }).finally(() => {
        this.buttonsDisabled = false;
      });
    },
    updateModel(newValue, field) {
      if(!field) return;
      this.model[field.name] = newValue[field.name];
    }
  },
  components: {
    FormRenderer,
    'app-modal': Modal
  }
}
</script>

<style>
  .modal-sml {
    overflow: unset!important;
  }
</style>